import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BedDouble, Pencil, Trash2, Plus, Check, X, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const bookingUrl = (stay) => {
  const aid = process.env.REACT_APP_BOOKING_AID;
  const ss = stay.name ? `${stay.name}, ${stay.location}` : stay.location;
  return `https://www.booking.com/searchresults.nl.html?ss=${encodeURIComponent(ss)}${aid ? `&aid=${aid}` : ""}`;
};

const StayRow = ({ stay, onSaved, onDeleted }) => {
  const [editing, setEditing] = useState(false);
  const [location, setLocation] = useState(stay.location);
  const [name, setName] = useState(stay.name || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (location.trim().length < 2) return toast.error("Vul een plaats of adres in");
    setBusy(true);
    try {
      await api.patch(`/saved-places/${stay.id}`, { location: location.trim(), name: name.trim() || null });
      toast.success("Overnachting bijgewerkt");
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/saved-places/${stay.id}`);
      toast.success(`${stay.location} verwijderd uit je lijst`);
      onDeleted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (editing) {
    return (
      <div data-testid={`stay-row-edit-${stay.id}`} className="bg-card rounded-2xl p-4 flex flex-col sm:flex-row gap-2 sm:items-center">
        <Input data-testid={`stay-edit-location-${stay.id}`} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Plaats of adres" className="rounded-xl h-10 flex-1" />
        <Input data-testid={`stay-edit-name-${stay.id}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Hotel / B&B (optioneel)" className="rounded-xl h-10 flex-1" />
        <div className="flex gap-1 shrink-0">
          <button data-testid={`stay-edit-save-${stay.id}`} onClick={save} disabled={busy} title="Opslaan"
            className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40 active:scale-95 transition-[transform] duration-200">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          </button>
          <button data-testid={`stay-edit-cancel-${stay.id}`} onClick={() => { setEditing(false); setLocation(stay.location); setName(stay.name || ""); }} title="Annuleren"
            className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center active:scale-95 transition-[transform] duration-200">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`stay-row-${stay.id}`} className="bg-card rounded-2xl p-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <span className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><BedDouble className="w-4 h-4 text-primary" /></span>
        <div className="min-w-0">
          <p className="font-heading font-bold text-sm truncate">{stay.location}</p>
          <p className="text-xs text-muted-foreground truncate">
            {stay.name || "Geen verblijfsnaam"} · {stay.use_count > 0 ? `${stay.use_count}× gebruikt` : "handmatig toegevoegd"}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <a data-testid={`stay-booking-${stay.id}`} href={bookingUrl(stay)} target="_blank" rel="noreferrer" title="Bekijk op Booking.com"
          className="rounded-full bg-[#003580]/10 text-[#003580] dark:bg-[#4a90d9]/15 dark:text-[#8db8e8] px-3 py-1.5 text-xs font-bold flex items-center gap-1 hover:bg-[#003580]/20 transition-[background-color] duration-200">
          Booking.com <ExternalLink className="w-3 h-3" />
        </a>
        <button data-testid={`stay-edit-${stay.id}`} onClick={() => setEditing(true)} title="Bewerken"
          className="p-2 rounded-full hover:bg-secondary text-primary transition-[background-color] duration-200">
          <Pencil className="w-4 h-4" />
        </button>
        <button data-testid={`stay-delete-${stay.id}`} onClick={remove} title="Verwijderen"
          className="p-2 rounded-full hover:bg-red-50 text-red-500 transition-[background-color] duration-200">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default function StaysPage() {
  const navigate = useNavigate();
  const [stays, setStays] = useState(null);
  const [location, setLocation] = useState("");
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/saved-places?kind=stay").then((r) => setStays(r.data)).catch(() => setStays([]));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (location.trim().length < 2) return toast.error("Vul een plaats of adres in");
    setAdding(true);
    try {
      await api.post("/saved-places", { location: location.trim(), name: name.trim() || null });
      toast.success("Toegevoegd aan je overnachtingen!");
      setLocation("");
      setName("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="stays-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <button data-testid="stays-back-button" onClick={() => navigate("/dashboard")}
          className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary mb-4 transition-[color] duration-200">
          <ArrowLeft className="w-3.5 h-3.5" /> Terug naar dashboard
        </button>
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-accent mb-2">Favoriete verblijven</p>
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mb-2">Mijn overnachtingen</h1>
        <p className="text-sm text-muted-foreground mb-8">Deze adressen verschijnen als één-klik-chips in de tripwizard bij "Eerder gebruikte overnachtingen". Verblijven uit je trips komen er automatisch bij; hier kun je ze ook zelf toevoegen, bewerken of verwijderen.</p>

        <form onSubmit={add} data-testid="add-stay-form" className="bg-card rounded-2xl p-4 flex flex-col sm:flex-row gap-2 sm:items-center mb-6">
          <Input data-testid="add-stay-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Plaats of adres (bv. Orléans, Frankrijk)" className="rounded-xl h-10 flex-1" />
          <Input data-testid="add-stay-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Hotel / B&B (optioneel)" className="rounded-xl h-10 flex-1" />
          <Button data-testid="add-stay-button" type="submit" disabled={adding} className="rounded-full bg-primary hover:bg-[#1B4332] shrink-0 h-10">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" /> Toevoegen</>}
          </Button>
        </form>

        {stays === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
        ) : stays.length === 0 ? (
          <div className="bg-card rounded-2xl p-8 text-center" data-testid="stays-empty">
            <BedDouble className="w-8 h-8 text-primary mx-auto mb-3" />
            <p className="font-heading font-bold mb-1">Nog geen overnachtingen</p>
            <p className="text-sm text-muted-foreground">Voeg hierboven een favoriet adres toe of plan een trip met eigen verblijven.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="stays-list">
            {stays.map((s) => <StayRow key={s.id} stay={s} onSaved={load} onDeleted={load} />)}
          </div>
        )}
      </main>
    </div>
  );
}
